import React, { useEffect, useState } from "react";
import { api, setAdminToken, getAdminToken } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus, Save, LogOut, Pencil, X, Copy, Link as LinkIcon, Send } from "lucide-react";

const empty_gig = { date: "", time: "", venue_name: "", venue_address: "", city: "", ticket_url: "", map_url: "", notes: "" };

const useList = (path) => {
  const [items, setItems] = useState([]);
  const reload = () => api.get(path).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []); // eslint-disable-line
  return [items, reload];
};

const SectionHeader = ({ title, count, children }) => (
  <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
    <div>
      <h2 className="text-2xl font-bold">{title}</h2>
      <p className="text-sm text-neutral-500">{count} total</p>
    </div>
    <div>{children}</div>
  </div>
);

const GigsAdmin = () => {
  const [gigs, reload] = useList("/gigs");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty_gig);
  const [show, setShow] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startNew = () => { setEditing(null); setForm(empty_gig); setShow(true); };
  const startEdit = (g) => {
    setEditing(g.id);
    setForm({
      date: g.date, time: g.time, venue_name: g.venue_name, venue_address: g.venue_address,
      city: g.city || "", ticket_url: g.ticket_url || "", map_url: g.map_url || "", notes: g.notes || "",
    });
    setShow(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/gigs/${editing}`, form);
        toast.success("Gig updated");
      } else {
        await api.post("/gigs", form);
        toast.success("Gig added");
      }
      setShow(false);
      reload();
    } catch {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this gig?")) return;
    await api.delete(`/gigs/${id}`);
    toast.success("Gig deleted");
    reload();
  };

  return (
    <div data-testid="admin-gigs">
      <SectionHeader title="Upcoming Gigs" count={gigs.length}>
        <Button onClick={startNew} data-testid="admin-gig-add"><Plus size={16} className="mr-2"/> Add Gig</Button>
      </SectionHeader>

      {show && (
        <Card className="mb-6 border-2 border-black">
          <CardHeader>
            <CardTitle>{editing ? "Edit Gig" : "Add Gig"}</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={onChange("date")} data-testid="admin-gig-date" /></div>
            <div><Label>Time</Label><Input value={form.time} onChange={onChange("time")} placeholder="8:00 PM" data-testid="admin-gig-time" /></div>
            <div><Label>Venue Name</Label><Input value={form.venue_name} onChange={onChange("venue_name")} data-testid="admin-gig-venue" /></div>
            <div><Label>City</Label><Input value={form.city} onChange={onChange("city")} data-testid="admin-gig-city" /></div>
            <div className="md:col-span-2"><Label>Venue Address</Label><Input value={form.venue_address} onChange={onChange("venue_address")} data-testid="admin-gig-address" /></div>
            <div><Label>Ticket URL</Label><Input value={form.ticket_url} onChange={onChange("ticket_url")} data-testid="admin-gig-ticket" /></div>
            <div><Label>Map URL</Label><Input value={form.map_url} onChange={onChange("map_url")} data-testid="admin-gig-map" /></div>
            <div className="md:col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={onChange("notes")} data-testid="admin-gig-notes" /></div>
            <div className="md:col-span-2 flex gap-2">
              <Button onClick={save} data-testid="admin-gig-save"><Save size={16} className="mr-2"/>Save</Button>
              <Button variant="outline" onClick={() => setShow(false)}><X size={16} className="mr-2"/>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {gigs.map((g) => (
          <Card key={g.id} data-testid={`admin-gig-row-${g.id}`}>
            <CardContent className="p-4 flex justify-between items-start gap-4 flex-wrap">
              <div>
                <p className="font-bold text-lg">{g.venue_name} <Badge variant="outline" className="ml-2">{g.date}</Badge> <span className="text-sm text-neutral-500">{g.time}</span></p>
                <p className="text-sm text-neutral-600">{g.venue_address}</p>
                {g.notes && <p className="text-xs text-neutral-500 mt-1">{g.notes}</p>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => startEdit(g)} data-testid={`admin-gig-edit-${g.id}`}><Pencil size={14}/></Button>
                <Button size="sm" variant="destructive" onClick={() => remove(g.id)} data-testid={`admin-gig-delete-${g.id}`}><Trash2 size={14}/></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const SimpleList = ({ title, path, render, testid }) => {
  const [items, reload] = useList(path);
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`${path}/${id}`);
    toast.success("Deleted");
    reload();
  };
  return (
    <div data-testid={testid}>
      <SectionHeader title={title} count={items.length} />
      {items.length === 0 && <p className="text-neutral-500 italic">Nothing yet.</p>}
      <div className="space-y-3">
        {items.map((it) => (
          <Card key={it.id} data-testid={`${testid}-row-${it.id}`}>
            <CardContent className="p-4 flex justify-between items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-0">{render(it)}</div>
              <Button size="sm" variant="destructive" onClick={() => remove(it.id)} data-testid={`${testid}-delete-${it.id}`}><Trash2 size={14}/></Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const PaymentsAdmin = () => {
  const [methods, reload] = useList("/payment-methods");
  const [draft, setDraft] = useState({});

  useEffect(() => {
    const d = {};
    methods.forEach((m) => { d[m.key] = { label: m.label, handle: m.handle, link: m.link, active: m.active }; });
    setDraft(d);
  }, [methods]);

  const save = async (key) => {
    try {
      await api.patch(`/payment-methods/${key}`, draft[key]);
      toast.success(`${draft[key].label} updated`);
      reload();
    } catch {
      toast.error("Save failed");
    }
  };

  return (
    <div data-testid="admin-payments">
      <SectionHeader title="Payment Methods" count={methods.length} />
      <div className="grid md:grid-cols-2 gap-4">
        {methods.map((m) => (
          <Card key={m.key} data-testid={`admin-payment-${m.key}`}>
            <CardHeader><CardTitle>{m.label}</CardTitle><CardDescription>{m.key}</CardDescription></CardHeader>
            <CardContent className="space-y-3">
              <div><Label>Handle</Label><Input value={draft[m.key]?.handle || ""} onChange={(e) => setDraft({ ...draft, [m.key]: { ...draft[m.key], handle: e.target.value } })} data-testid={`admin-payment-handle-${m.key}`} /></div>
              <div><Label>Link</Label><Input value={draft[m.key]?.link || ""} onChange={(e) => setDraft({ ...draft, [m.key]: { ...draft[m.key], link: e.target.value } })} data-testid={`admin-payment-link-${m.key}`} /></div>
              <Button onClick={() => save(m.key)} data-testid={`admin-payment-save-${m.key}`}><Save size={14} className="mr-2"/>Save</Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const MediaAdmin = () => {
  const [media, reload] = useList("/media");
  const [form, setForm] = useState({ type: "photo", title: "", url: "", description: "" });

  const add = async () => {
    if (!form.title || !form.url) { toast.error("Title and URL required"); return; }
    try {
      await api.post("/media", form);
      toast.success("Media added");
      setForm({ type: "photo", title: "", url: "", description: "" });
      reload();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/media/${id}`);
    reload();
  };

  return (
    <div data-testid="admin-media">
      <SectionHeader title="Media" count={media.length} />
      <Card className="mb-6">
        <CardHeader><CardTitle>Add Media</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Type</Label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full border rounded px-3 py-2" data-testid="admin-media-type">
              <option value="photo">Photo</option>
              <option value="video">Video (embed URL)</option>
              <option value="press">Press / Quote</option>
            </select>
          </div>
          <div><Label>Title / Quote</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-media-title" /></div>
          <div className="md:col-span-2"><Label>URL</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="admin-media-url" /></div>
          <div className="md:col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-media-desc" /></div>
          <div><Button onClick={add} data-testid="admin-media-add"><Plus size={14} className="mr-2"/>Add</Button></div>
        </CardContent>
      </Card>
      <div className="grid md:grid-cols-2 gap-3">
        {media.map((m) => (
          <Card key={m.id} data-testid={`admin-media-row-${m.id}`}>
            <CardContent className="p-4 flex gap-3">
              {m.type === "photo" && <img src={m.url} alt={m.title} className="w-20 h-20 object-cover" />}
              <div className="flex-1">
                <p className="font-bold">{m.title}</p>
                <p className="text-xs text-neutral-500">{m.type}</p>
              </div>
              <Button size="sm" variant="destructive" onClick={() => remove(m.id)} data-testid={`admin-media-delete-${m.id}`}><Trash2 size={14}/></Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const InvitesAdmin = () => {
  const [invites, reload] = useList("/admin/invites");
  const [form, setForm] = useState({ label: "", days_valid: 30, max_uses: 50 });
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/invites", form);
      toast.success("Invite link created");
      setForm({ label: "", days_valid: 30, max_uses: 50 });
      reload();
    } catch {
      toast.error("Could not create invite");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this upload link?")) return;
    await api.delete(`/admin/invites/${id}`);
    toast.success("Invite revoked");
    reload();
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/upload/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste in the band chat");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
  };

  return (
    <div data-testid="admin-invites">
      <SectionHeader title="Band Upload Invites" count={invites.length} />
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Create new upload link</CardTitle>
          <CardDescription>Share this link with band members so they can drop photos straight into the Media gallery — no password required.</CardDescription>
        </CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-4">
          <div>
            <Label>Label (private note)</Label>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Band group chat" data-testid="invite-label" />
          </div>
          <div>
            <Label>Days valid</Label>
            <Input type="number" min="1" value={form.days_valid} onChange={(e) => setForm({ ...form, days_valid: Number(e.target.value) || 30 })} data-testid="invite-days" />
          </div>
          <div>
            <Label>Max uploads on this link</Label>
            <Input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: Number(e.target.value) || 50 })} data-testid="invite-max" />
          </div>
          <div className="md:col-span-3">
            <Button onClick={create} disabled={busy} data-testid="invite-create"><Plus size={14} className="mr-2"/>{busy ? "Creating..." : "Create link"}</Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {invites.length === 0 && <p className="text-neutral-500 italic">No active invites. Create one above.</p>}
        {invites.map((inv) => {
          const url = `${window.location.origin}/upload/${inv.token}`;
          const expired = new Date(inv.expires_at) < new Date();
          const full = (inv.used_count || 0) >= (inv.max_uses || 0);
          return (
            <Card key={inv.id} data-testid={`admin-invite-row-${inv.id}`}>
              <CardContent className="p-4 flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold">{inv.label || "(no label)"}</p>
                    {expired && <Badge variant="destructive">Expired</Badge>}
                    {!expired && full && <Badge variant="destructive">Full</Badge>}
                    {!expired && !full && <Badge variant="outline">Active</Badge>}
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">Expires {fmtDate(inv.expires_at)} · {inv.used_count}/{inv.max_uses} used</p>
                  <code className="block mt-2 text-[11px] text-neutral-700 break-all">{url}</code>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => copyLink(inv.token)} data-testid={`admin-invite-copy-${inv.id}`}><Copy size={14} className="mr-2"/>Copy</Button>
                  <Button size="sm" variant="outline" asChild>
                    <a href={`/upload/${inv.token}`} target="_blank" rel="noreferrer" data-testid={`admin-invite-open-${inv.id}`}><LinkIcon size={14} className="mr-2"/>Open</a>
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => revoke(inv.id)} data-testid={`admin-invite-revoke-${inv.id}`}><Trash2 size={14}/></Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="mt-8 p-4 border border-neutral-200 bg-neutral-50 text-sm flex items-start gap-3">
        <Send size={16} className="mt-1 shrink-0"/>
        <p>Want a quick share message? Paste this in the band chat: <br/><em>"Hey, drop photos from tonight's show here — no password needed: <code>[paste link]</code>"</em></p>
      </div>
    </div>
  );
};

const SubmissionsAdmin = () => {
  const [subs, reload] = useList("/admin/media-submissions");

  const decide = async (token, action) => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${base}/api/media-submissions/decide/${token}?action=${action}`);
      if (!r.ok && r.status !== 410) {
        toast.error("Action failed");
        return;
      }
      toast.success(action === "approve" ? "Approved" : "Rejected");
      reload();
    } catch {
      toast.error("Action failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission and its media item (if approved)?")) return;
    await api.delete(`/admin/media-submissions/${id}`);
    toast.success("Deleted");
    reload();
  };

  const pending = subs.filter((s) => s.status === "pending");
  const decided = subs.filter((s) => s.status !== "pending");

  const renderItem = (s) => (
    <Card key={s.id} data-testid={`admin-sub-row-${s.id}`}>
      <CardContent className="p-4 flex gap-4 items-start flex-wrap">
        <div className="w-24 h-24 shrink-0 bg-neutral-100 border border-neutral-200 flex items-center justify-center overflow-hidden">
          {s.type === "photo" && s.storage_path ? (
            <img src={`/api/files/${s.storage_path}`} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[10px] font-mono uppercase text-neutral-500">{s.type}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold">{s.title}</p>
            <Badge variant={s.status === "pending" ? "outline" : s.status === "approved" ? "default" : "destructive"}>{s.status}</Badge>
          </div>
          <p className="text-xs text-neutral-500 mt-1">From: <b>{s.submitter_name}</b> {s.submitter_email && <span>· {s.submitter_email}</span>}</p>
          {s.caption && <p className="text-xs italic mt-1">"{s.caption}"</p>}
          {s.video_url && <a href={s.video_url} target="_blank" rel="noreferrer" className="text-xs underline break-all">{s.video_url}</a>}
          <p className="text-[10px] text-neutral-400 mt-1">{new Date(s.created_at).toLocaleString()}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          {s.status === "pending" && (
            <>
              <Button size="sm" onClick={() => decide(s.approval_token, "approve")} data-testid={`admin-sub-approve-${s.id}`}>Approve</Button>
              <Button size="sm" variant="outline" onClick={() => decide(s.approval_token, "reject")} data-testid={`admin-sub-reject-${s.id}`}>Reject</Button>
            </>
          )}
          <Button size="sm" variant="destructive" onClick={() => remove(s.id)} data-testid={`admin-sub-delete-${s.id}`}><Trash2 size={14}/></Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div data-testid="admin-submissions">
      <SectionHeader title="Fan Submissions" count={subs.length}>
        {pending.length > 0 && <Badge>{pending.length} pending</Badge>}
      </SectionHeader>

      <h3 className="text-sm font-bold uppercase tracking-wider mb-3 text-neutral-500">Pending review</h3>
      <div className="space-y-3">
        {pending.length === 0 && <p className="text-neutral-400 italic">No pending submissions.</p>}
        {pending.map(renderItem)}
      </div>

      {decided.length > 0 && (
        <>
          <h3 className="text-sm font-bold uppercase tracking-wider mt-10 mb-3 text-neutral-500">History</h3>
          <div className="space-y-3 opacity-80">
            {decided.map(renderItem)}
          </div>
        </>
      )}
    </div>
  );
};

const LoginForm = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/login", { password });
      setAdminToken(r.data.token);
      toast.success("Welcome back");
      onSuccess();
    } catch {
      toast.error("Wrong password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-neutral-950">
      <form onSubmit={submit} className="border border-white/15 p-10 w-full max-w-md" data-testid="admin-login-form">
        <p className="eyebrow">Admin</p>
        <h1 className="font-heading text-4xl uppercase mt-3">Retrofit CMS</h1>
        <p className="text-neutral-400 mt-3 text-sm">Enter the band password to manage gigs, requests, bookings, payments and media.</p>
        <div className="mt-8">
          <label className="label-retro">Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="field" autoFocus data-testid="admin-login-password" />
        </div>
        <button type="submit" disabled={busy} className="btn-retro-solid mt-8 w-full disabled:opacity-50" data-testid="admin-login-submit">
          {busy ? "Checking..." : "Sign in"}
        </button>
      </form>
    </div>
  );
};

const Admin = () => {
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = getAdminToken();
    if (!t) { setLoading(false); return; }
    api.get("/admin/verify").then(() => setAuthed(true)).catch(() => setAdminToken(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-white">Loading...</div>;
  if (!authed) return <LoginForm onSuccess={() => setAuthed(true)} />;

  const logout = async () => {
    try {
      await api.post("/admin/logout");
    } catch (err) {
      console.warn("Logout request failed:", err);
    }
    setAdminToken(null);
    setAuthed(false);
  };

  return (
    <div className="admin-light min-h-screen bg-white text-black" data-testid="admin-dashboard">
      <header className="border-b border-neutral-200 bg-white sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs font-mono tracking-[0.25em] uppercase text-neutral-500">Retrofit Admin</p>
            <h1 className="text-xl font-bold">Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild><a href="/" data-testid="admin-view-site">View site</a></Button>
            <Button variant="outline" onClick={logout} data-testid="admin-logout"><LogOut size={14} className="mr-2"/>Sign out</Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8">
        <Tabs defaultValue="gigs" className="w-full">
          <TabsList className="flex flex-wrap h-auto justify-start mb-6">
            <TabsTrigger value="gigs" data-testid="tab-gigs">Gigs</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests">Song Requests</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="playlists" data-testid="tab-playlists">Playlists</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
            <TabsTrigger value="submissions" data-testid="tab-submissions">Submissions</TabsTrigger>
            <TabsTrigger value="invites" data-testid="tab-invites">Invites</TabsTrigger>
          </TabsList>

          <TabsContent value="gigs"><GigsAdmin /></TabsContent>
          <TabsContent value="requests">
            <SimpleList title="Song Requests" path="/song-requests" testid="admin-requests" render={(it) => (
              <div>
                <p className="font-bold">{it.song_title} — <span className="text-neutral-600">{it.artist}</span></p>
                <p className="text-sm">From: {it.name} {it.event && `· ${it.event}`}</p>
                {it.message && <p className="text-xs text-neutral-500 mt-1 italic">"{it.message}"</p>}
                <p className="text-[10px] text-neutral-400 mt-1">{new Date(it.created_at).toLocaleString()}</p>
              </div>
            )} />
          </TabsContent>
          <TabsContent value="bookings">
            <SimpleList title="Booking Inquiries" path="/bookings" testid="admin-bookings" render={(it) => (
              <div>
                <p className="font-bold">{it.name} — {it.event_type}</p>
                <p className="text-sm">{it.event_date} · {it.event_location} · ~{it.guest_count || "?"} guests</p>
                <p className="text-sm text-neutral-600">{it.email} · {it.phone}</p>
                <p className="text-xs text-neutral-500">Budget: {it.budget_range || "—"} · Length: {it.performance_length || "—"} · {it.indoor_outdoor || "—"}</p>
                {it.special_requests && <p className="text-xs mt-1 italic">"{it.special_requests}"</p>}
                <p className="text-[10px] text-neutral-400 mt-1">{new Date(it.created_at).toLocaleString()}</p>
              </div>
            )} />
          </TabsContent>
          <TabsContent value="playlists">
            <SimpleList title="Client Playlists" path="/playlists" testid="admin-playlists" render={(it) => (
              <div>
                <p className="font-bold">{it.client_name} — {it.event_date}</p>
                {it.must_play && <p className="text-sm"><b>Must play:</b> {it.must_play}</p>}
                {it.do_not_play && <p className="text-sm"><b>Do not play:</b> {it.do_not_play}</p>}
                {it.first_dance && <p className="text-sm"><b>First dance:</b> {it.first_dance}</p>}
                {it.spotify_link && <a href={it.spotify_link} target="_blank" rel="noreferrer" className="text-xs underline">Spotify</a>}
                {it.apple_link && <a href={it.apple_link} target="_blank" rel="noreferrer" className="text-xs underline ml-3">Apple</a>}
                {it.notes && <p className="text-xs italic mt-1">"{it.notes}"</p>}
                <p className="text-[10px] text-neutral-400 mt-1">{new Date(it.created_at).toLocaleString()}</p>
              </div>
            )} />
          </TabsContent>
          <TabsContent value="contacts">
            <SimpleList title="Contact Messages" path="/contacts" testid="admin-contacts" render={(it) => (
              <div>
                <p className="font-bold">{it.name} — {it.subject || "(no subject)"}</p>
                <p className="text-sm text-neutral-600">{it.email}</p>
                <p className="text-sm mt-1">{it.message}</p>
                <p className="text-[10px] text-neutral-400 mt-1">{new Date(it.created_at).toLocaleString()}</p>
              </div>
            )} />
          </TabsContent>
          <TabsContent value="payments"><PaymentsAdmin /></TabsContent>
          <TabsContent value="media"><MediaAdmin /></TabsContent>
          <TabsContent value="submissions"><SubmissionsAdmin /></TabsContent>
          <TabsContent value="invites"><InvitesAdmin /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
